import { theme, extendTheme as chakraExtendTheme, } from "@chakra-ui/react";
import { CompassColor, Size, Spacing } from "./constants";
import { generateColorPalette } from "./utils";
const compassTheme = {
    config: {
        useSystemColorMode: false,
        initialColorMode: "light",
    },
    layerStyles: {},
    colors: {
        primary: {
            "50": "#ffecef",
            100: "#ffd0d4",
            200: "#f3a09d",
            300: "#ea7b77",
            400: "#f55e54",
            500: "#fb513b",
            600: "#ec473a",
            700: "#da3e34",
            800: "#cc372d",
            900: "#bd2d21",
        },
        secondary: generateColorPalette(CompassColor.white),
        info: {
            50: "#def5f6",
            100: "#aee5e9",
            200: "#7bd4dc",
            300: "#47c2ce",
            400: "#22b6c4",
            500: "#00a9bb",
            600: "#009aaa",
            700: "#038691",
            800: "#05727a",
            900: "#085051",
        },
        success: {
            50: "#f2f7e7",
            100: "#deecc3",
            200: "#c8df9c",
            300: "#b2d273",
            400: "#a1c953",
            500: "#90bf31",
            600: "#81b02a",
            700: "#81b02a",
            800: "#578816",
            900: "#326700",
        },
        warning: {
            50: "#fdfbe4",
            100: "#f9f3bd",
            200: "#f5eb91",
            300: "#f1e465",
            400: "#efdf41",
            500: "#ecd913",
            600: "#ebc70c",
            700: "#eab000",
            800: "#e89700",
            900: "#e56d00",
        },
        error: {
            50: "#fdecee",
            100: "#fbcfd1",
            200: "#e89f9a",
            300: "#dc7b73",
            400: "#e46051",
            500: "#e75538",
            600: "#d94c37",
            700: "#c74331",
            800: "#ba3d2b",
            900: "#aa3621",
        },
        // Generated using https://materialpalettes.com/
        gray: {
            "50": "#fafafa",
            "100": "#f4f4f4",
            "200": "#ededed",
            "300": "#dedede",
            "400": "#bbbbbb",
            "500": "#9c9c9c",
            "600": "#737373",
            "700": "#5f5f5f",
            "800": "#404040",
            "900": "#1f1f1f",
        },
    },
    fonts: {
        body: '"Untitled Sans", sans-serif',
        heading: '"Untitled Serif", sans-serif',
        mono: '"Brown LL", sans-serif',
    },
    components: {
        Button: {
            baseStyle: {
                borderRadius: "8px",
                fontWeight: "medium",
            },
            sizes: {
                xl: {
                    height: 16,
                    minWidth: 16,
                    fontSize: "xl",
                    px: 12,
                },
                lg: {
                    height: 12,
                    minWidth: 12,
                    fontSize: "lg",
                    px: 8,
                },
                md: {
                    height: 10,
                    minWidth: 10,
                    fontSize: "md",
                    px: 6,
                },
                sm: {
                    height: 8,
                    minWidth: 8,
                    fontSize: "sm",
                    px: 4,
                },
                xs: {
                    height: 6,
                    minWidth: 6,
                    fontSize: "xs",
                    px: 2,
                },
            },
        },
        CompassLoader: {
            baseStyle: {
                layer1: generateColorPalette("#EB7000"),
                layer2: generateColorPalette("#FFB81D"),
                layer3: generateColorPalette("#FFCF04"),
            },
            sizes: {
                xs: { w: "1rem", h: "1rem" },
                sm: { w: "2rem", h: "2rem" },
                md: { w: "4rem", h: "4rem" },
                lg: { w: "7rem", h: "7rem" },
                xl: { w: "10rem", h: "10rem" },
                "2xl": { w: "15rem", h: "15rem" },
                "3xl": { w: "30rem", h: "30rem" },
            },
        },
        ClientLoader: {
            sizes: {
                xs: { w: "1rem", h: "1rem" },
                sm: { w: "2rem", h: "2rem" },
                md: { w: "4rem", h: "4rem" },
                lg: { w: "7rem", h: "7rem" },
                xl: { w: "10rem", h: "10rem" },
                "2xl": { w: "15rem", h: "15rem" },
                "3xl": { w: "30rem", h: "30rem" },
            },
        },
        Input: {
            variants: {
                outline: {
                    field: {
                        bg: "white",
                    },
                },
            },
        },
        Modal: {
            parts: ["header", "footer", "body"],
            baseStyle: {
                dialog: {
                    borderRadius: "8px",
                    bg: CompassColor.white,
                    boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.16)",
                },
                overlay: {
                    bg: "rgba(0, 0, 0, 0.48)",
                },
                header: {
                    fontWeight: "500",
                    textAlign: "center",
                    borderBottom: "1px",
                    borderBottomStyle: "solid",
                    borderColor: CompassColor.sand01,
                    color: CompassColor.black,
                },
                body: {
                    margin: Spacing[5],
                    paddingInline: 0,
                    color: CompassColor.black,
                    fontSize: Size.md,
                },
                footer: {
                    justifyContent: "space-evenly",
                    paddingInline: 0,
                    marginLeft: Spacing[5],
                    marginRight: Spacing[5],
                },
            },
        },
        Select: {
            baseStyle: {
                borderRadius: "8px",
            },
        },
        NoomLoader: {
            baseStyle: {
                layer1: generateColorPalette("#EB7000"),
                layer2: generateColorPalette("#FFB81D"),
                layer3: generateColorPalette("#FFCF04"),
            },
            sizes: {
                xs: { w: "1rem", h: "1rem" },
                sm: { w: "2rem", h: "2rem" },
                md: { w: "4rem", h: "4rem" },
                lg: { w: "7rem", h: "7rem" },
                xl: { w: "10rem", h: "10rem" },
                "2xl": { w: "15rem", h: "15rem" },
                "3xl": { w: "30rem", h: "30rem" },
            },
        },
        Popover: {
            variants: {
                responsive: {
                    popper: {
                        maxWidth: "unset",
                        width: "unset",
                    },
                },
            },
        },
        Tag: {
            sizes: {
                sm: { container: { borderRadius: "0.3rem" } },
                md: { container: { borderRadius: "0.35rem" } },
                lg: { container: { borderRadius: "0.35rem" } },
            },
        },
        Tooltip: {
            baseStyle: {
                color: "white",
            },
        },
        Heading: {
            baseStyle: {
                fontWeight: 500,
            },
        },
    },
};
export function extendCompassTheme(customTheme) {
    return chakraExtendTheme(theme, compassTheme, customTheme);
}
export default chakraExtendTheme(theme, compassTheme);
//# sourceMappingURL=compassTheme.js.map